// Shared timezone + channel helpers for the Publish surface.
// P5.1 (2026-11-03) — Roger's acceptance review turned up calendar/queue
// times that were silently rendering in UTC on his phone and inconsistent
// channel names ("Facebook" vs "instagram"). This module centralizes the
// rules so every page agrees:
//
//   • Times are formatted in the org timezone (loaded from
//     /organizations/current/settings) using Intl.DateTimeFormat with
//     timeZone + timeZoneName, so DST is handled correctly by the browser.
//   • The datetime-local <input> is interpreted in the SAME timezone,
//     not the user's device zone. A slot entered as "Oct 31 7am ET" and
//     a slot entered as "Nov 2 7am ET" both produce the correct UTC
//     instant across the Nov-1 DST switch.
//   • Channel display names are canonical: Facebook, Instagram, YouTube,
//     TikTok, Pinterest, Email, Manual Export.

import { useEffect, useState } from "react";
import { api } from "./api";

export const DEFAULT_TZ = "America/New_York";

export const CHANNEL_DISPLAY = {
  facebook:       "Facebook",
  instagram:      "Instagram",
  youtube:        "YouTube",
  tiktok:         "TikTok",
  pinterest:      "Pinterest",
  email:          "Email",
  manual_export:  "Manual Export",
};

export function channelDisplay(ch) {
  if (!ch) return "—";
  const key = String(ch).toLowerCase().trim();
  if (CHANNEL_DISPLAY[key]) return CHANNEL_DISPLAY[key];
  // Unknown channel — Title Case the raw id instead of leaking "instagram".
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/* -------------------------------------------------------------------------- */
/*  Timezone context — one network call per page load                         */
/* -------------------------------------------------------------------------- */

let _cachedTz = null;        // { timezone, abbr, offsetMinutes } or null
let _cachedTzPromise = null; // in-flight promise
const _subscribers = new Set();

function _notify() {
  for (const fn of _subscribers) {
    try { fn(_cachedTz); } catch (_) { /* ignore */ }
  }
}

export async function fetchOrgTimezone({ force = false } = {}) {
  if (!force && _cachedTz) return _cachedTz;
  if (!force && _cachedTzPromise) return _cachedTzPromise;
  _cachedTzPromise = (async () => {
    try {
      const r = await api.get("/organizations/current/settings");
      const d = r.data || {};
      _cachedTz = {
        timezone:       d.timezone || DEFAULT_TZ,
        abbr:           d.timezone_abbr || "",
        offsetMinutes:  d.timezone_utc_offset_minutes ?? null,
      };
    } catch (_e) {
      _cachedTz = { timezone: DEFAULT_TZ, abbr: "", offsetMinutes: null };
    } finally {
      _cachedTzPromise = null;
      _notify();
    }
    return _cachedTz;
  })();
  return _cachedTzPromise;
}

/** React hook — returns `{ timezone, abbr, offsetMinutes }`. Falls back to
 *  the default while the first fetch is in flight so no component crashes. */
export function useOrgTimezone() {
  const [tz, setTz] = useState(
    _cachedTz || { timezone: DEFAULT_TZ, abbr: "", offsetMinutes: null },
  );
  useEffect(() => {
    if (!_cachedTz) {
      let cancel = false;
      fetchOrgTimezone().then((v) => { if (!cancel) setTz(v); });
      return () => { cancel = true; };
    }
    // Keep subscribed so a settings edit refreshes everyone.
    _subscribers.add(setTz);
    return () => { _subscribers.delete(setTz); };
  }, []);
  return tz;
}

/** Mutators call this after a successful PUT so subscribers refresh. */
export async function refreshOrgTimezone() {
  await fetchOrgTimezone({ force: true });
  return _cachedTz;
}

/* -------------------------------------------------------------------------- */
/*  Formatting                                                                */
/* -------------------------------------------------------------------------- */

function _resolveTz(tzMaybe) {
  if (!tzMaybe) {
    return _cachedTz ? _cachedTz.timezone : DEFAULT_TZ;
  }
  if (typeof tzMaybe === "string") return tzMaybe;
  if (tzMaybe.timezone) return tzMaybe.timezone;
  return DEFAULT_TZ;
}

// P5.3 (2026-11-03) — Defense in depth. If the API ever returns a
// naive ISO string (no offset, no ``Z``) — e.g. because an older
// collection still stores naive datetimes — the browser's native
// ``new Date(iso)`` parses it as *local* time per ECMAScript 2015+.
// That used to produce "1:00 PM EDT" for a 13:00 UTC schedule on a
// US/Eastern laptop. We now treat any offset-less ISO as UTC, which
// matches our server storage policy.
//
// Rules:
//   * If the string already carries an offset (``Z``, ``+hh:mm``,
//     ``-hh:mm``), honour it.
//   * If it is a bare date-time (``YYYY-MM-DDTHH:MM:SS[.sss]``),
//     append ``Z`` before parsing.
//   * Anything else is returned as-is so the native parser can raise.
const _HAS_OFFSET_RE = /(Z|[+\-]\d{2}:?\d{2})$/;
export function parseIsoAsUtc(iso) {
  if (iso instanceof Date) return iso;
  if (typeof iso !== "string") return new Date(NaN);
  const trimmed = iso.trim();
  if (!trimmed) return new Date(NaN);
  if (_HAS_OFFSET_RE.test(trimmed)) return new Date(trimmed);
  // Naive datetime: pin to UTC.
  return new Date(`${trimmed}Z`);
}

/** "Nov 2, 2026, 7:00 AM EST" */
export function fmtDateTimeTz(iso, tzMaybe) {
  if (!iso) return "—";
  const tz = _resolveTz(tzMaybe);
  const d = parseIsoAsUtc(iso);
  if (isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year:     "numeric",
      month:    "short",
      day:      "numeric",
      hour:     "numeric",
      minute:   "2-digit",
      hour12:   true,
      timeZoneName: "short",
    }).format(d);
  } catch (_e) {
    return d.toISOString();
  }
}

/** "7:00 AM EST" — compact time + zone abbr. */
export function fmtTimeTz(iso, tzMaybe) {
  if (!iso) return "—";
  const tz = _resolveTz(tzMaybe);
  const d = parseIsoAsUtc(iso);
  if (isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour:     "numeric",
      minute:   "2-digit",
      hour12:   true,
      timeZoneName: "short",
    }).format(d);
  } catch (_e) {
    return d.toISOString();
  }
}

/** "Nov 2, 2026" — month/day/year in the org zone. */
export function fmtDateTz(iso, tzMaybe) {
  if (!iso) return "—";
  const tz = _resolveTz(tzMaybe);
  const d = parseIsoAsUtc(iso);
  if (isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year:     "numeric",
      month:    "short",
      day:      "numeric",
    }).format(d);
  } catch (_e) {
    return d.toISOString();
  }
}

/** "Mon, Nov 2" for agenda headings. */
export function fmtWeekdayDateTz(iso, tzMaybe) {
  if (!iso) return "—";
  const tz = _resolveTz(tzMaybe);
  const d = parseIsoAsUtc(iso);
  if (isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday:  "short",
      month:    "short",
      day:      "numeric",
    }).format(d);
  } catch (_e) {
    return d.toISOString();
  }
}

/** Return the ISO date-key (YYYY-MM-DD) *in the org zone* so Calendar
 *  bucketing agrees with the time rendered on each chip. */
export function dateKeyInTz(iso, tzMaybe) {
  if (!iso) return "";
  const tz = _resolveTz(tzMaybe);
  const d = parseIsoAsUtc(iso);
  if (isNaN(d.getTime())) return "";
  // en-CA locale yields YYYY-MM-DD, side-stepping m/d/y ambiguity.
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch (_e) {
    return "";
  }
}

/** Pull {year, month, day} integers for a given UTC/ISO value in a tz. */
export function partsInTz(iso, tzMaybe) {
  const tz = _resolveTz(tzMaybe);
  const d = iso instanceof Date ? iso : parseIsoAsUtc(iso);
  if (!d || isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    year:   Number(parts.year),
    month:  Number(parts.month),
    day:    Number(parts.day),
    hour:   Number(parts.hour || 0),
    minute: Number(parts.minute || 0),
  };
}

/** Format a Date into a `datetime-local` input value as it would appear
 *  in the org's tz. */
export function toDatetimeLocalInTz(iso, tzMaybe) {
  const p = partsInTz(iso, tzMaybe);
  if (!p) return "";
  const z = (n) => String(n).padStart(2, "0");
  return `${p.year}-${z(p.month)}-${z(p.day)}T${z(p.hour)}:${z(p.minute)}`;
}

/** Parse a datetime-local input (e.g. "2026-11-02T07:00") that the user
 *  typed in the org's timezone and return the matching UTC Date.
 *  DST-correct: the method computes the UTC time whose rendering in the
 *  org timezone equals the typed wall-clock, iterating twice because
 *  partsInTz is the only reliable way to invert Intl.DateTimeFormat in
 *  the browser.
 */
export function parseDatetimeLocalInTz(value, tzMaybe) {
  if (!value) return null;
  const tz = _resolveTz(tzMaybe);
  // Parse as if the local parts were UTC; adjust by the offset we see
  // when the browser renders that UTC moment IN the target zone.
  const [datePart, timePart] = String(value).split("T");
  if (!datePart || !timePart) return null;
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;

  // 1st guess: assume the typed value is UTC.
  let guess = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
  // Iterate up to 3 times to converge across DST boundaries.
  for (let i = 0; i < 3; i++) {
    const p = partsInTz(guess, tz);
    if (!p) return guess;
    const deltaMin =
      (p.year - y) * 525600 +
      (p.month - mo) * 43800 +
      (p.day - d) * 1440 +
      (p.hour - h) * 60 +
      (p.minute - mi);
    if (deltaMin === 0) return guess;
    guess = new Date(guess.getTime() - deltaMin * 60000);
  }
  return guess;
}

/** "7:00 AM EST" chip-level time for the Calendar chip. */
export function fmtChipTime(iso, tzMaybe) {
  return fmtTimeTz(iso, tzMaybe);
}
